import { FC, useMemo } from "react";

import { Box, Skeleton } from "@mui/material";

import { useGetServicesByNode } from "../hooks/useGetServicesByNode";
import DeviceModelChipDropdown from "../../../common/components/chip/DeviceModelChipDropdown";
import { ServiceTypeEnum } from "../../model-manager/variables/modelManager";
import { IDeviceTableRow, IModel } from "../hooks/useDeviceTableRows";
import AddModelButton from "./AddModelStyledButton";
import RouterLink from "../../../common/components/RouterLink";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

interface IConnectedModelsColumnProps {
  device: IDeviceTableRow;
}

const DeviceConnectedModelsColumn: FC<IConnectedModelsColumnProps> = ({
  device,
}) => {
  const { data, loading } = useGetServicesByNode(device.node.id);

  const services = useMemo(() => {
    return data?.getServicesByNode?.items.filter(service => {
      const serviceDeviceId = "DE#" + service.deviceId?.split("#DE#")[1];

      return serviceDeviceId === device.rowId;
    });
  }, [data, device.rowId]);

  const showAddModelButton =
    device.modelsToRun?.length > (services?.length ?? 0);

  const memoizedRunningLeakModel = useMemo(() => {
    const runningLeakModel = services?.find(
      m =>
        m.isRunning &&
        (m.serviceType === ServiceTypeEnum.LEAK ||
          m.serviceType === ServiceTypeEnum.GAS_LEAK)
    );

    return runningLeakModel;
  }, [services]);

  const memoizedRunningLiquidLeakModel = useMemo(() => {
    const runningLiquidLeakModel = services?.find(
      m => m.isRunning && m.serviceType === ServiceTypeEnum.LIQUID_LEAK
    );

    return runningLiquidLeakModel;
  }, [services]);

  const memoizedRunningLeakModels = useMemo(() => {
    let runningLeakModels: string[] = [];

    if (memoizedRunningLeakModel) {
      runningLeakModels = [memoizedRunningLeakModel.serviceType ?? ""];
    }

    if (memoizedRunningLiquidLeakModel) {
      runningLeakModels = [memoizedRunningLiquidLeakModel.serviceType ?? ""];
    }

    if (memoizedRunningLeakModel && memoizedRunningLiquidLeakModel) {
      runningLeakModels = [
        memoizedRunningLeakModel.serviceType ?? "",
        memoizedRunningLiquidLeakModel.serviceType ?? "",
      ];
    }

    return runningLeakModels;
  }, [memoizedRunningLeakModel, memoizedRunningLiquidLeakModel]);

  if (loading) {
    return <Skeleton variant="text" width={100} height={20} />;
  }

  return (
    <>
      {services?.map((model): JSX.Element => {
        const updatedModel = {
          ...model,
          serviceName: model.serviceType as ServiceTypeEnum,
          serviceId: model.id as string,
        } as IModel;

        return (
          <DeviceModelChipDropdown
            key={model.id}
            data={device}
            model={updatedModel}
            runningLeakModels={memoizedRunningLeakModels}
          />
        );
      })}

      <Box
        sx={{
          visibility: showAddModelButton ? "visible" : "hidden",
        }}
      >
        {showAddModelButton && (
          <RouterLink
            to={"/device/" + encodeURIComponent(device.rowId)}
            state={{ deviceDatum: device }}
          >
            <AddModelButton
              className="addModelButton"
              disableRipple
              startIcon={<AddRoundedIcon />}
            />
          </RouterLink>
        )}
      </Box>
    </>
  );
};

export default DeviceConnectedModelsColumn;
